/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SourceType } from 'client-code/channels-service';
import { ContentType } from 'client-code/grid-guide';

import { DeviceModel } from '../../../core/device';
import { Channel, OneLineGuideCell } from '../../../core/entities';
import { GuideType, InteractiveComponents } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Log } from '../../../core/logging';
import { CursorManager } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/playback/enums';
import { SharedPrefConstants } from '../../../core/storage';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2, TOfferPreview, TOfferPreviewCustomData } from '../../../core/types';
import { DateTime } from '../../../core/utils';
import { isJumpChannelCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { onLongKeyOkEmptyGuide } from '../../guide/emptyguide/emptyguide.navigation.reducers';
import { launchVodScreen } from '../../jumpchannel/jumpchannel.reducers';
import { showChannelOptionsOverlay } from '../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { OverlayTypes } from '../../overlay/OverlayTypes';
import { showPCPinOverlayWithProgramRating } from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { showRecordOverlay } from '../../overlay/recordoverlay/recordoverlay.reducers';
import { triggerPlaybackOnPinVerify } from '../../videoplayer/videoplayer.reducers';
import { showTrickPlayMenu } from '../infobanner/infobanner.reducers';
import {
    setOneLineGuideAreChannelsSelectedAction,
    setOneLineGuideCellSelectedAction,
    setOneLineGuideHorizontalScrollAction,
    setOneLineGuideIsFutureModeAction,
    setOneLineGuideSelectedChannelAction,
    setOneLineGuideTimeStamp,
    setOneLineGuideTurboScrollAction,
} from './onelineguide.actions';
import {
    ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE,
    ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
    TAG,
} from './onelineguide.constants';
import {
    getMetadataForChannel,
    getOneLineGuideChannels,
    getOneLineGuideFutureThreshold,
    getOneLineGuidePastThreshold,
    getOneLineGuideRowMetadata,
    getSingleChannelByChannelId,
    hideOneLineGuide,
    showOneLineGuide,
    updateOneLineGuideInactivityTimeout,
} from './onelineguide.reducers';

const DEBOUNCE_VALUE = 300; // ms
let showsDebounceTimeout: NodeJS.Timeout;

export const onKeyUpOneLineGuide =
    (getMetadata = true, numberOfChannels: number = 1) =>
    (dispatch: Function, getState: TGetState) => {
        const {
            channels,
            selectedChannel,
            showOneLineGuide: isOneLineGuideShown,
            isEmpty,
        } = getState().oneLineGuide;
        if (!isOneLineGuideShown) {
            /*
             * There are moments when the screen task controller
             * is set to ONE_LINE_GUIDE even if it is not opened.
             * This usually happens when there was a streaming
             * error caused by poor internet connection, because
             * when the internet is stable again, the screen task
             * is set to the last one before the error.
             */
            dispatch(showOneLineGuide());
            return;
        }

        if (isEmpty || channels.length === 1) return;

        const currentChannelIndex = channels.findIndex(
            (channel: TChannelV2 | Channel) => channel.channelId === selectedChannel?.channelId,
        );

        const navigationIncrement =
            channels.length < ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE ? 1 : numberOfChannels;

        let nextChannelIndex = currentChannelIndex - navigationIncrement;
        if (nextChannelIndex < 0) {
            nextChannelIndex = channels.length - Math.abs(nextChannelIndex);
        }
        dispatch(setOneLineGuideSelectedChannelAction(channels[nextChannelIndex]));
        dispatch(getOneLineGuideChannels(channels[nextChannelIndex]));
        if (getMetadata) {
            dispatch(debouncedMetadataRequest(channels[nextChannelIndex]));
        }
        dispatch(updateOneLineGuideInactivityTimeout());
    };

export const onLongKeyUpOneLineGuide = () => (dispatch: Function) => {
    dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.NORTH));
};

export const onKeyDownOneLineGuide =
    (getMetadata = true, numberOfChannels: number = 1) =>
    (dispatch: Function, getState: TGetState) => {
        const { channels, selectedChannel, isEmpty } = getState().oneLineGuide;
        const currentChannelIndex = channels.findIndex(
            (channel: TChannelV2 | Channel) => channel.channelId === selectedChannel?.channelId,
        );

        if (isEmpty || channels.length === 1) return;

        const navigationIncrement =
            channels.length < ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE ? 1 : numberOfChannels;

        const nextChannelIndex = (currentChannelIndex + navigationIncrement) % channels.length;
        dispatch(setOneLineGuideSelectedChannelAction(channels[nextChannelIndex]));
        dispatch(getOneLineGuideChannels(channels[nextChannelIndex]));
        if (getMetadata) {
            dispatch(debouncedMetadataRequest(channels[nextChannelIndex]));
        }
        dispatch(updateOneLineGuideInactivityTimeout());
    };

export const onLongKeyDownOneLineGuide = () => (dispatch: Function) => {
    dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.SOUTH));
};

export const onKeyLeftOneLineGuide = () => (dispatch: Function, getState: TGetState) => {
    const {
        selectedChannel,
        cellList,
        currentSelectedCell,
        currentAiringCell,
        isContentFetched,
        areChannelsSelected,
        isFutureMode,
        error,
    } = getState().oneLineGuide;

    // This is the case when the OLG is opened and the first request fails.
    // We ensure that the user can still navigate to the channel list.
    if (isServiceDownOnOpen(error, cellList, currentSelectedCell)) {
        if (isFutureMode) {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        }
        return;
    }

    const isLeftKeyActionAvailable =
        (currentSelectedCell !== null || currentAiringCell !== null) &&
        isHorizontalKeyActionAvailableOLG(
            dispatch,
            cellList,
            currentSelectedCell,
            currentAiringCell,
            'arrowLeftOneLineGuide',
        );
    if (!isLeftKeyActionAvailable) {
        Log.i(TAG, 'onKeyLeftOneLineGuide: Action not available');
        return;
    }

    const currentSelectedCellIndex = currentSelectedCell
        ? cellList.indexOf(currentSelectedCell)
        : -1;
    const currentAiringCellIndex = currentAiringCell ? cellList.indexOf(currentAiringCell) : -1;

    // for past olg, go back to strip from channel list
    if (currentSelectedCellIndex + 1 === currentAiringCellIndex && areChannelsSelected) {
        dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        dispatch(setOneLineGuideIsFutureModeAction(false));
        return;
    }

    if (currentSelectedCellIndex < currentAiringCellIndex && isFutureMode) {
        dispatch(setOneLineGuideAreChannelsSelectedAction(true));
        return;
    }

    const isOpenedInDisconnectState = error && cellList.length === 0;

    // for future olg, decide whether to go in past or give control to channel list
    if (currentSelectedCellIndex === currentAiringCellIndex) {
        if (areChannelsSelected && !isOpenedInDisconnectState) {
            dispatch(setOneLineGuideIsFutureModeAction(false));
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
            return;
        }
    }

    if (currentSelectedCellIndex - 1 < 0) return;
    dispatch(setOneLineGuideCellSelectedAction(cellList[currentSelectedCellIndex - 1]));

    let newTimeStamp: DateTime;
    if (currentAiringCell === cellList[currentSelectedCellIndex - 1]) {
        newTimeStamp = new DateTime();
    } else if (
        cellList[currentSelectedCellIndex - 1].startTime.getTime() < getOneLineGuidePastThreshold()
    ) {
        newTimeStamp = new DateTime(getOneLineGuidePastThreshold());
    } else {
        newTimeStamp = cellList[currentSelectedCellIndex - 1].startTime;
    }
    dispatch(setOneLineGuideTimeStamp(newTimeStamp));

    const firstCellTime: DateTime = cellList[0].startTime.ceilByMinutes(30);

    if (firstCellTime.getTime() < getOneLineGuidePastThreshold()) {
        return;
    }

    if (currentSelectedCellIndex > ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER) return;

    if (isContentFetched || !selectedChannel) return;

    dispatch(
        getOneLineGuideRowMetadata(
            selectedChannel,
            firstCellTime,
            ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
            true,
        ),
    );
};

export const onKeyRightOneLineGuide = () => (dispatch: Function, getState: TGetState) => {
    const {
        selectedChannel,
        cellList,
        currentSelectedCell,
        currentAiringCell,
        isContentFetched,
        areChannelsSelected,
        isFutureMode,
        error,
    } = getState().oneLineGuide;

    // This is the case when the OLG is opened and the first request fails.
    // We ensure that the user can still navigate to the right dummy channel cell.
    if (isServiceDownOnOpen(error, cellList, currentSelectedCell)) {
        if (isFutureMode) {
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
        }
        return;
    }

    const isRightKeyActionAvailable =
        (currentSelectedCell !== null || currentAiringCell !== null) &&
        isHorizontalKeyActionAvailableOLG(
            dispatch,
            cellList,
            currentSelectedCell,
            currentAiringCell,
            'arrowRightOneLineGuide',
        );
    if (!isRightKeyActionAvailable) {
        Log.i(TAG, 'onKeyRightOneLineGuide: Action not available');
        return;
    }

    const currentSelectedCellIndex = currentSelectedCell
        ? cellList.indexOf(currentSelectedCell)
        : -1;
    const currentAiringCellIndex = currentAiringCell ? cellList.indexOf(currentAiringCell) : -1;

    // for future olg, go back to strip from channel list
    if (currentSelectedCellIndex === currentAiringCellIndex && areChannelsSelected) {
        dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        return;
    }

    // for past olg, decide whether to go in future or give control to channel list
    if (!isFutureMode && currentSelectedCellIndex + 1 === currentAiringCellIndex) {
        if (areChannelsSelected) {
            dispatch(setOneLineGuideIsFutureModeAction(true));
            dispatch(setOneLineGuideAreChannelsSelectedAction(false));
        } else {
            dispatch(setOneLineGuideAreChannelsSelectedAction(true));
            return;
        }
    }

    if (currentSelectedCellIndex + 1 >= cellList.length) return;
    dispatch(setOneLineGuideCellSelectedAction(cellList[currentSelectedCellIndex + 1]));

    let newTimeStamp: DateTime;
    if (currentAiringCell === cellList[currentSelectedCellIndex + 1]) {
        newTimeStamp = new DateTime();
    } else if (
        cellList[currentSelectedCellIndex + 1].startTime.getTime() >
        getOneLineGuideFutureThreshold()
    ) {
        newTimeStamp = new DateTime(getOneLineGuideFutureThreshold());
    } else {
        newTimeStamp = cellList[currentSelectedCellIndex + 1].startTime;
    }
    dispatch(setOneLineGuideTimeStamp(newTimeStamp));

    const lastCellTime: DateTime = cellList[cellList.length - 1].startTime.floorByMinutes(30);

    if (lastCellTime.getTime() > getOneLineGuideFutureThreshold()) {
        return;
    }

    const cellCountUntilEndOfList: number = cellList.length - currentSelectedCellIndex;
    if (cellCountUntilEndOfList > ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER) return;

    if (isContentFetched || !selectedChannel) return;

    dispatch(
        getOneLineGuideRowMetadata(
            selectedChannel,
            lastCellTime,
            ONE_LINE_GUIDE_FETCH_ASSETS_NUMBER,
            false,
        ),
    );
};

export const onKeyOkOneLineGuide =
    (isLongPress = false) =>
    async (dispatch: Function, getState: TGetState) => {
        const { channelOptionsOverlay, oneLineGuide } = getState();
        const { channelsType } = channelOptionsOverlay;
        const {
            areChannelsSelected,
            selectedChannel,
            currentSelectedCell,
            currentAiringCell,
            offerPreview: offerPreviewPromise,
            isEmpty,
            error,
            cellList,
        } = oneLineGuide;

        // This is the case when the OLG is opened and the first request fails.
        // We ensure that the user can still open the channel options overlay.
        if (
            isServiceDownOnOpen(error, cellList, currentSelectedCell) &&
            areChannelsSelected &&
            selectedChannel?.channelId
        ) {
            dispatch(hideOneLineGuide());
            const currentChannel = await getSingleChannelByChannelId(
                selectedChannel.channelId,
                channelsType,
            );
            dispatch(
                showChannelOptionsOverlay(
                    currentChannel ?? selectedChannel,
                    GuideType.ONE_LINE_GUIDE,
                ),
            );
            return;
        }

        if (isEmpty || currentSelectedCell === null) return;

        if (
            isJumpChannelCcbSwitchEnabled() &&
            selectedChannel?.sourceType === SourceType.JumpChannel &&
            selectedChannel.channelNumber
        ) {
            dispatch(
                launchVodScreen(
                    selectedChannel.channelNumber,
                    {
                        onError: () => dispatch(hideOneLineGuide()),
                    },
                    OverlayTypes.OLG_JUMP_CHANNEL_ERROR_OVERLAY,
                ),
            );
            Log.i(TAG, 'onKeyOkOneLineGuide: Launched VOD screen for Jump Channel');
            return;
        }

        const offerPreview = await getCurrentPreview(offerPreviewPromise, currentSelectedCell);

        dispatch(hideOneLineGuide());
        if (
            areChannelsSelected &&
            selectedChannel?.channelId &&
            !NetworkManager.isNetworkDisconnected()
        ) {
            updateRecordingSharedPreferenceBeforeOpeningOverlay(currentSelectedCell);
            const currentChannel = await getSingleChannelByChannelId(
                selectedChannel.channelId,
                channelsType,
            );
            dispatch(
                showChannelOptionsOverlay(
                    currentChannel ?? selectedChannel,
                    GuideType.ONE_LINE_GUIDE,
                ),
            );
        } else if (
            currentSelectedCell === currentAiringCell &&
            !isLongPress &&
            selectedChannel &&
            !NetworkManager.isNetworkDisconnected()
        ) {
            VideoPlaybackController.getInstance().startPlayback({
                channel: selectedChannel,
                playbackContentType: PlaybackContentType.LIVE,
            });
            dispatch(showTrickPlayMenu());
        } else if (!NetworkManager.isNetworkDisconnected() && selectedChannel) {
            const guideCell = currentSelectedCell;

            const isExplicitlyAdult = offerPreview.contentPreview?.isAdult ?? false;
            const isContentPCViolated = new ServicesClientImpl().checkPCViolations({
                offerPreview,
            });
            const invokeRecordOverlay = () => async () => {
                await dispatch(
                    showRecordOverlay({
                        channel: selectedChannel,
                        guideCell,
                        offerPreview,
                        showMetaData: false,
                    }),
                );
                if (isExplicitlyAdult) dispatch(triggerPlaybackOnPinVerify());
            };
            updateRecordingSharedPreferenceBeforeOpeningOverlay(currentSelectedCell);
            if (isExplicitlyAdult && isContentPCViolated) {
                dispatch(showPCPinOverlayWithProgramRating(invokeRecordOverlay, isExplicitlyAdult));
            } else {
                dispatch(invokeRecordOverlay());
            }
        }
    };

export const onLongKeyOkOneLineGuide = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory, oneLineGuide } = getState();
    const { value } = inputFactory;
    const { isEmpty } = oneLineGuide;

    if (isEmpty) {
        dispatch(onLongKeyOkEmptyGuide(GuideType.ONE_LINE_GUIDE));
        return;
    }

    if (CursorManager.isCursorAppeared()) {
        switch (value) {
            case InteractiveComponents.WEST:
                dispatch(onKeyLeftOneLineGuide());
                break;
            case InteractiveComponents.EAST:
                dispatch(onKeyRightOneLineGuide());
                break;
            default:
                dispatch(onKeyOkOneLineGuide(true));
                break;
        }
    } else {
        dispatch(onKeyOkOneLineGuide(true));
    }
};

export const onMouseWheelOneLineGuide = (wheelDeltaY: number) => (dispatch: Function) => {
    if (wheelDeltaY < 0) dispatch(onKeyUpOneLineGuide());
    if (wheelDeltaY > 0) dispatch(onKeyDownOneLineGuide());
};

export const onMouseClickOneLineGuide = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (value) {
        case InteractiveComponents.NORTH:
            dispatch(onKeyUpOneLineGuide());
            break;
        case InteractiveComponents.SOUTH:
            dispatch(onKeyDownOneLineGuide());
            break;
        case InteractiveComponents.WEST:
            dispatch(onKeyLeftOneLineGuide());
            break;
        case InteractiveComponents.EAST:
            dispatch(onKeyRightOneLineGuide());
            break;
        default:
            dispatch(onKeyOkOneLineGuide());
            break;
    }
};

export const onLongClickOneLineGuide = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;

    switch (value) {
        case InteractiveComponents.NORTH:
            dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.NORTH));
            break;
        case InteractiveComponents.SOUTH:
            dispatch(setOneLineGuideTurboScrollAction(true, InteractiveComponents.SOUTH));
            break;
        case InteractiveComponents.WEST:
            dispatch(setOneLineGuideHorizontalScrollAction(InteractiveComponents.WEST));
            break;
        case InteractiveComponents.EAST:
            dispatch(setOneLineGuideHorizontalScrollAction(InteractiveComponents.EAST));
            break;
        default:
            dispatch(onLongKeyOkOneLineGuide());
            break;
    }
};

export const onLongClickStopOneLineGuide =
    () =>
    (dispatch: Function, getState: TGetState): void => {
        const { oneLineGuide } = getState();
        const { oneLineGuideTurboScroll, timeStamp, selectedChannel } = oneLineGuide;

        dispatch(setOneLineGuideHorizontalScrollAction(null));
        if (oneLineGuideTurboScroll && selectedChannel && timeStamp) {
            dispatch(getMetadataForChannel(selectedChannel, timeStamp));
            dispatch(setOneLineGuideTurboScrollAction(false));
        }
    };

export const onLongKeyStopOneLineGuide =
    () =>
    (dispatch: Function, getState: Function): void => {
        const { oneLineGuide } = getState();
        const { oneLineGuideTurboScroll, timeStamp, selectedChannel } = oneLineGuide;

        if (oneLineGuideTurboScroll) {
            dispatch(getMetadataForChannel(selectedChannel, timeStamp));
            dispatch(setOneLineGuideTurboScrollAction(false));
        }
    };

const getCurrentPreview = async (
    offerPreviewPromise: Promise<TOfferPreview> | null,
    currentSelectedCell: OneLineGuideCell,
): Promise<TOfferPreviewCustomData> => {
    try {
        if (!offerPreviewPromise) throw new Error('No offer preview promise');
        const { offerPreview } = await offerPreviewPromise;
        return offerPreview;
    } catch {
        const offerPreview: TOfferPreviewCustomData = {};
        offerPreview.contentPreview = {
            ...currentSelectedCell,
            previewType:
                currentSelectedCell.contentType === ContentType.Episode
                    ? 'EpisodePreview'
                    : 'MoviePreview',
            startTime: currentSelectedCell.startTime.toISOString(),
        };
        return offerPreview;
    }
};

const debouncedMetadataRequest =
    (nextChannel: Channel) => (dispatch: Function, getState: Function) => {
        const { timeStamp } = getState().oneLineGuide;

        if (showsDebounceTimeout) {
            clearTimeout(showsDebounceTimeout);
        }
        showsDebounceTimeout = setTimeout(() => {
            dispatch(getMetadataForChannel(nextChannel, timeStamp));
        }, DEBOUNCE_VALUE);
    };

const isHorizontalKeyActionAvailableOLG = (
    dispatch: Function,
    cellList: OneLineGuideCell[],
    currentSelectedCell: OneLineGuideCell | null,
    currentAiringCell: OneLineGuideCell | null,
    tag: 'arrowLeftOneLineGuide' | 'arrowRightOneLineGuide',
) => {
    Diagnostics.info(TAG, tag);
    Log.i(TAG, tag);

    dispatch(updateOneLineGuideInactivityTimeout());

    const currentSelectedCellIndex = currentSelectedCell
        ? cellList.indexOf(currentSelectedCell)
        : -1;
    const currentAiringCellIndex = currentAiringCell ? cellList.indexOf(currentAiringCell) : -1;
    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

    if (
        (currentSelectedCellIndex === -1 && currentAiringCellIndex === -1) ||
        isNetworkDisconnected
    ) {
        return false;
    }
    return true;
};

export const updateRecordingSharedPreferenceBeforeOpeningOverlay = (
    currentSelectedCell: OneLineGuideCell,
) => {
    const recordingSharedPreferenceEditor = DeviceModel.getSharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    ).edit();

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_OFFER_ID,
        currentSelectedCell.offerId,
    );

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_CONTENT_ID,
        currentSelectedCell.contentId,
    );
    recordingSharedPreferenceEditor.commit();
};

const isServiceDownOnOpen = (
    error: any,
    cellList: OneLineGuideCell[],
    currentSelectedCell: OneLineGuideCell | null,
) => {
    return (
        error &&
        cellList.length === 0 &&
        currentSelectedCell === null &&
        !NetworkManager.isNetworkDisconnected()
    );
};
